import PropTypes from 'prop-types';
import React, {Fragment, memo, useMemo} from 'react';
import {AppBar, Box, Grid, Toolbar} from '@mui/material';

import AppsMenu from './AppsMenu.jsx';
import ProductTitle from './ProductTitle.jsx';
import TitleChip from './TitleChip.jsx';
import UserMenu from './UserMenu.jsx';

/**
 * Navigation bar for use as header in Planet apps
 */
export const NavBar = ({
  children,
  title,
  user,
  logout,
  login,
  primary,
  secondary,
  appsMenuProps,
  userMenuProps,
  sx,
  titleChipProps,
  ...props
}) => {
  const showSentinelHubApps = useMemo(() => Boolean(user?.sh_user_id), [user]);

  return (
    <AppBar
      sx={[
        {
          zIndex: theme => theme.zIndex.drawer + 1,
          backgroundImage: 'none',
          boxShadow: theme => (theme.palette.mode === 'dark' ? 'none' : ''),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    >
      <Toolbar
        variant="dense"
        component={Grid}
        container
        disableGutters
        sx={{py: 0, px: 1}}
      >
        {children ? (
          children
        ) : (
          <Fragment>
            <Grid direction="row" container item xs={8} alignItems="center">
              <Grid item xs={6}>
                {primary}
              </Grid>
              <Grid
                item
                xs={6}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ProductTitle title={title} divider={!!title} />
                {titleChipProps && <TitleChip {...titleChipProps} />}
              </Grid>
            </Grid>

            <Grid item xs={4}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                flexWrap="nowrap"
              >
                {secondary}
                <AppsMenu
                  {...appsMenuProps}
                  includeSentinelHubApps={showSentinelHubApps}
                />
                {(user || login || logout) && (
                  <UserMenu
                    user={user}
                    logout={logout}
                    login={login}
                    {...userMenuProps}
                  />
                )}
              </Box>
            </Grid>
          </Fragment>
        )}
      </Toolbar>
    </AppBar>
  );
};

NavBar.propTypes = {
  /**
   App title
  */
  title: PropTypes.string,

  /**
   Customize contents of App Bar
  */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   Primary area
  */
  primary: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   Secondary area
  */
  secondary: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   Logged in User for User Menu
  */
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    name: PropTypes.string,
    sh_user_id: PropTypes.string,
  }),
  /**
   Logout function for User Menu
  */
  logout: PropTypes.func,
  /**
   Login function for User Menu
  */
  login: PropTypes.func,
  /**
   Additional Props for the Apps Menu
  */
  appsMenuProps: PropTypes.object,
  /**
   Additional Props for the User Menu
  */
  userMenuProps: PropTypes.object,

  /**
   * Override NavBar styles
   */
  sx: PropTypes.object,

  /**
   * Chip Props to display a chip next to the title
   */
  titleChipProps: PropTypes.object,
};

export default memo(NavBar);
